/**
 * Die Ja/Nein-Karte des Schmieds mit Kennung — die Wörter zwischen Server, Seite und Hörer. (20.10.2026, Martin 12:38)
 *
 * Martin: „Das Ja muss natürlich Bezug nehmen auf die jeweilige Frage. Das muss natürlich verknüpft werden.“ Bisher kam ein Knopf-Ja beim
 * Schmied als nackte Zeile „MARTIN: Ja“ an — bei zwei offenen Karten nicht zuzuordnen. Jetzt:
 *   - Der SERVER vergibt jeder Ja/Nein-Karte des Schmieds eine Kennung (6 Hex, `metadata.karte` / Bus-Feld `karte`, server/dreier-routen.ts).
 *   - Martins Tipp geht an KARTE_PFAD (server/schmied-karte-route.ts): nur Cookie des Betreiberkontos + Kopf der eigenen Seite, Karte bekannt,
 *     höchstens 24 h alt, noch offen. Der Server speichert dann Martins Wort („Ja“/„Nein“) mit `metadata.karteAntwort` = { kennung, antwort,
 *     frage } — den Bezug setzt NUR der Server, nie ein Text. Jede Karte einmal (idempotent); zwei Karten sind unabhängig.
 *   - Der Hörer (scripts/dreier-hoeren.mjs) druckt daraus „{karte k-ab12cd} MARTIN: JA zur Frage „…“ (Knopf)“.
 *   - 20.10.2026 (Martin 22:07): auch Zimmermann und Hüter stellen solche Karten (`--ja-nein`). Die Karte kennt am Server ihren Absender;
 *     Martins Tipp geht NUR an ihn (`an: <Absender>` + `karteAntwort`), sein Hörer druckt dieselbe Zeile. Je Zusatz-Entwickler höchstens eine
 *     offene Karte (eine neue ersetzt die vorige: Fehler „ersetzt“) und höchstens KARTEN_JE_STUNDE_ZUSATZ neue Karten je Stunde.
 * Diese Datei hat keine Node-Abhängigkeit (die Seite importiert sie).
 */
import { RUECKFRAGE_ID_MUSTER } from "./entwickler-im-chat.js";

export const KARTE_PFAD = "/api/dreier/karte";
/**
 * 30.10.2026 (Martin): der ZWEITE Weg — der Knopf im Entwickler-Panel auf diesem Server (server/schmied-karte-panel-route.ts). Nur Loopback,
 * Panel-Schlüssel im Kopf statt Cookie, nur Karten des Schmieds. Die Antwort trägt `quelle: "panel"`.
 */
export const KARTE_PANEL_PFAD = "/api/dreier/karte-panel";
/** Der Kopf, in dem das Panel seinen Schlüssel schickt (der Wert erscheint nie in einer Antwort oder im Journal). */
export const KARTE_PANEL_KOPF = "x-panel-schluessel";
/** Der Kopf der eigenen Seite (server/eigene-seite.ts, Kopf `x-vespera-aktion`) mit eigenem Wert. */
export const KARTE_KOPF_WERT = "schmied-karte";
/** Eine Karte verfällt nach 24 Stunden (wie die Rückfrage des Zimmermanns). */
export const KARTE_HOECHSTALTER_MS = 24 * 60 * 60 * 1000;
/** 20.10.2026: höchstens so viele neue Karten je Stunde je Zusatz-Entwickler (Zimmermann, Hüter). */
export const KARTEN_JE_STUNDE_ZUSATZ = 6;
/** So viele Zeichen der Frage reisen mit der Antwort (Hörerzeile, Journal nie). */
export const KARTE_FRAGE_ZEICHEN = 60;

export type KarteAntwort = "ja" | "nein";

/** Steuer- und Richtungszeichen (bidi, Null-Breite) — fliegen aus jedem Fragetext, bevor er beim Hörer steht. */
// eslint-disable-next-line no-control-regex -- gerade die Steuerzeichen sollen weg
export const STEUERZEICHEN = /[\u0000-\u001f\u007f-\u009f\u061c\u200b-\u200f\u202a-\u202e\u2066-\u2069]/g;

/**
 * Prüfauflage 20.10.2026 (A3): Zeichen, die eine Karten-FRAGE nie enthalten darf — Steuerzeichen außer Zeilenwechsel/Tab, C1, Null-Breite,
 * Richtungsmarken/-umschalter, Zeilen-/Absatztrenner, BOM. Martin bestätigt genau den Text, den er sieht; Unsichtbares darf ihn nicht verändern.
 */
// eslint-disable-next-line no-control-regex -- gerade die Steuerzeichen sollen erkannt werden
export const KARTE_UNSICHTBAR = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u061c\u200b-\u200f\u2028\u2029\u202a-\u202e\u2066-\u2069\ufeff]/;

/** Feste Fehlergründe der Route — die Seite übersetzt sie in Worte (nie Text vom Server anzeigen). */
export const KARTE_FEHLER = [
  "aus",
  "nicht-erlaubt",
  "herkunft-unzulaessig",
  "not-logged-in",
  "auth-revoked",
  "ungueltig",
  "dreier-aus",
  "unbekannt",
  "abgelaufen",
  "beantwortet",
  "ersetzt",
  "limit",
  "nicht-bereit",
] as const;
export type KarteFehlerCode = (typeof KARTE_FEHLER)[number];

/** Die Kennung einer Karte, streng (6 Hex-Zeichen) — sonst `null`. Rein. */
export function karteIdAus(wert: unknown): string | null {
  return typeof wert === "string" && RUECKFRAGE_ID_MUSTER.test(wert) ? wert : null;
}

export interface KarteBezug {
  kennung: string;
  antwort: KarteAntwort;
  /** Der Anfang der Frage (höchstens KARTE_FRAGE_ZEICHEN, geschwärzt, ohne Steuerzeichen) — gesetzt vom Server. */
  frage: string;
  /** 30.10.2026: über welchen Weg Martin tippte — fehlt = Knopf in der App; „panel“ = Knopf im Entwickler-Panel. Gesetzt NUR vom Server. */
  quelle?: "panel";
}

/** `metadata.karteAntwort` / Bus-Feld `karteAntwort` einer Nachricht von Martin, streng geprüft — sonst `null`. Rein. */
export function karteBezugAus(wert: unknown): KarteBezug | null {
  if (typeof wert !== "object" || wert === null || Array.isArray(wert)) return null;
  const w = wert as { kennung?: unknown; antwort?: unknown; frage?: unknown; quelle?: unknown };
  const kennung = karteIdAus(w.kennung);
  const antwort = w.antwort === "ja" || w.antwort === "nein" ? w.antwort : null;
  if (!kennung || !antwort) return null;
  const frage = typeof w.frage === "string" ? w.frage.replace(STEUERZEICHEN, " ").slice(0, KARTE_FRAGE_ZEICHEN + 1) : "";
  return { kennung, antwort, frage, ...(w.quelle === "panel" ? { quelle: "panel" as const } : {}) };
}

/** Das Wort, das als Martins Nachricht gespeichert wird (wie bisher der Knopf). */
export function karteWort(antwort: KarteAntwort): "Ja" | "Nein" {
  return antwort === "ja" ? "Ja" : "Nein";
}

/** Die Wörter auf der Karte je Fehler (Text kommt nie vom Server). */
export function karteFehlerText(code: string): string {
  switch (code) {
    case "beantwortet":
      return "Diese Frage ist schon beantwortet.";
    case "ersetzt":
      return "Zu dieser Frage gibt es schon eine neuere Karte.";
    case "abgelaufen":
      return "Die Frage ist abgelaufen (älter als 24 Stunden).";
    case "unbekannt":
      return "Diese Frage kennt der Server nicht (mehr).";
    case "dreier-aus":
      return "„Entwickler im Chat“ ist aus.";
    case "limit":
      return "Zu viele Antworten, bitte gleich noch einmal.";
    case "not-logged-in":
    case "auth-revoked":
      return "Bitte neu anmelden.";
    default:
      return "Senden fehlgeschlagen, bitte noch einmal tippen.";
  }
}
